/**
 * Bounded PDP route-authorization I/O for the plugin routers.
 *
 * Both plugin routers (`PluginActivityRouter`, `DynamicRoutes`) evaluate the
 * routes a plugin contributes against the PDP during their "Checking
 * permissions" load step. This module owns *only* the network call — building
 * the request, enforcing a hard timeout, and parsing the response — so that:
 *
 *  - a slow / booting / unreachable PDP can never hang the loader (the fetch is
 *    always bounded by {@link PDP_EVAL_TIMEOUT_MS}), and
 *  - the failure semantics are uniform and testable (any non-2xx, network
 *    error, or timeout yields `evaluations: null` so callers fail closed).
 *
 * The security-critical allow/deny decision mapping stays in each router — this
 * helper never decides authorization, it only fetches and parses.
 */

/** A single AuthZEN evaluation entry as sent to `/access/v1/evaluations`. */
export interface PdpRouteEval {
	subject: unknown;
	resource: unknown;
	action: unknown;
	context: unknown;
}

/** Outcome of a bounded PDP evaluations call. */
export interface PdpEvalOutcome {
	/** HTTP status, or `0` for a network error / timeout / abort. */
	status: number;
	/** True only for a 2xx response with a parsed body. */
	ok: boolean;
	/** Raw response, for callers that need it (e.g. 401 re-auth handling). Null on network failure. */
	response: Response | null;
	/** Parsed evaluations array, or `null` when the call failed (callers must fail closed). */
	evaluations: unknown[] | null;
	/** `X-Request-ID` for correlation, empty string when absent. */
	requestId: string;
}

/**
 * Hard ceiling for the PDP route-authorization call ("Checking permissions"
 * step). Generous enough for a cold PDP, but bounded so the plugin loader
 * always resolves instead of spinning forever.
 */
export const PDP_EVAL_TIMEOUT_MS = 15000;

/** Read the CSRF token the BFF requires for state-changing requests. */
function readCsrfToken(): string {
	if (typeof document === 'undefined') return '';
	return document.cookie.match(/(?:^|; )_eid_csrf_v1=([^;]+)/)?.[1] || '';
}

/**
 * POST plugin route authorization evaluations to the PDP, bounded by a timeout.
 *
 * Never throws: on any failure (non-2xx, network error, or timeout) it returns
 * `{ ok: false, evaluations: null }` so callers can fail closed.
 *
 * @param evals      the evaluations to submit
 * @param timeoutMs  hard timeout for the request (defaults to {@link PDP_EVAL_TIMEOUT_MS})
 * @param fetchImpl  injectable fetch (for testing; defaults to global `fetch`)
 */
export async function postRouteEvaluations(
	evals: PdpRouteEval[],
	timeoutMs: number = PDP_EVAL_TIMEOUT_MS,
	fetchImpl: typeof fetch = fetch,
): Promise<PdpEvalOutcome> {
	const csrf = readCsrfToken();
	try {
		const response = await fetchImpl('/access/v1/evaluations', {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-Token': csrf,
				'X-CSRFToken': csrf,
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: JSON.stringify({ evaluations: evals }),
			// Bound the call so a stalled PDP can't hang the loader indefinitely.
			signal: AbortSignal.timeout(timeoutMs),
		});

		const requestId = response.headers.get('X-Request-ID') || '';
		if (!response.ok) {
			return { status: response.status, ok: false, response, evaluations: null, requestId };
		}

		const result = await response.json();
		const evaluations =
			result?.evaluations || result?.decisions || result?.results || result || [];
		return { status: response.status, ok: true, response, evaluations, requestId };
	} catch {
		// Network error, AbortSignal.timeout, or a body-parse failure — fail closed.
		return { status: 0, ok: false, response: null, evaluations: null, requestId: '' };
	}
}
